import classnames from 'classnames/bind'
import { useMemo, useRef, useState } from 'react'
import { useInterval } from 'react-use'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { SHOPIFY_TYPES } from '~/lib/shopify/constants'

import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import RichText, { RichTextBlocks } from '~/components/Abstracts/RichText'
import SliderNavigation, {
  SliderNavigationType,
} from '~/components/Navigation/SliderNavigation'
import Image, { ImageProps } from '~/components/UI/Image'

import { useGlobalData } from '~/providers/GlobalDataProvider'
import { useStyle } from '~/providers/StyleProvider'

import useWindowActive from '~/hooks/useWindowActive'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const mapPrismicTypeToType = {
  [SHOPIFY_TYPES.PRODUCT_FRAGRANCE]: 'fragrance',
  [SHOPIFY_TYPES.PRODUCT_FASHION]: 'fashion',
  [SHOPIFY_TYPES.PRODUCT_SET]: 'sets',
}

export interface ProductSliderReassuranceProps {
  className?: string
  itemClassName?: string
  items?: ProductSliderItemProps[]
  type?: string
}
export interface ProductSliderItemProps {
  className?: string
  image?: ImageProps
  name?: RichTextBlocks
  label?: RichTextBlocks
}

const IMAGE_SIZES = [{ ratio: 1 / 24 }]

function ProductSliderReassurance({
  className,
  items,
  type,
}: ProductSliderReassuranceProps) {
  const { global } = useGlobalData()

  const nameStyle = useStyle({
    textPreset: GlobalTextPreset.Label11_12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const labelStyle = useStyle({
    textPreset: GlobalTextPreset.Label11_12Haffer,
    color: GlobalThemeColors.DoveGray,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const customSliderRef = useRef()
  const windowActive = useWindowActive()
  const [autoPlay, setAutoPlay] = useState(true)

  const [
    { slideIndex, maxSlideIndex, setSlideIndex, nextSlide },
    setSliderState,
  ] = useSliderState()

  const processedItems = useMemo(
    () =>
      items ||
      global?.productReassurances?.[mapPrismicTypeToType[type]]?.items ||
      null,
    [global, type, items],
  )

  useInterval(nextSlide, autoPlay && windowActive ? 2500 : null)

  const disableAutoPlay = () => setAutoPlay(false)
  const enableAutoPlay = () => setAutoPlay(true)

  return processedItems ? (
    <div
      ref={customSliderRef}
      className={cx(css.ProductSliderReassurance, className)}
      onTouchStart={disableAutoPlay}
      onTouchEnd={enableAutoPlay}
      onMouseEnter={disableAutoPlay}
      onMouseLeave={enableAutoPlay}>
      {processedItems?.length > 0 && (
        <Slider
          className={css.slider}
          snap={true}
          infinite={true}
          setSliderState={setSliderState}
          customSliderRef={customSliderRef}>
          {processedItems?.map(({ image, name, label }, index) => (
            <div key={`${name}_${index}`} className={cx(css.item)}>
              <div className={cx(css.top)}>
                {image && (
                  <Image
                    className={cx(css.image)}
                    sizesFromBreakpoints={IMAGE_SIZES}
                    layout="responsive"
                    {...image}
                  />
                )}

                <RichText className={cx(css.name, nameStyle)} render={name} />
              </div>

              <div className={cx(css.bottom)}>
                <RichText
                  className={cx(css.label, labelStyle)}
                  render={label}
                />
              </div>
            </div>
          ))}
        </Slider>
      )}
      {processedItems?.length > 1 && (
        <SliderNavigation
          className={cx(css.sliderNavigation, css.hideOnLargeScreen)}
          type={SliderNavigationType.DotSmall}
          max={maxSlideIndex + 1}
          index={slideIndex}
          setIndex={setSlideIndex}
        />
      )}
    </div>
  ) : null
}

ProductSliderReassurance.defaultProps = {}

export default ProductSliderReassurance
